import { useRef, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Check, CircleHelp, Eye, FileUp, Pencil, X } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import type { BadgeProps } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DataTable } from "@/components/data/DataTable";
import { EmptyState } from "@/components/feedback/EmptyState";
import { toast } from "@/components/feedback/ToastHost";
import { errorMessage } from "@/lib/errors";
import { Permission } from "@/lib/permissions";
import { usePermissions } from "@/hooks/usePermissions";
import {
  decideInvoiceDocument,
  listInvoiceDocuments,
  updateInvoiceDocument,
  uploadInvoicePdf,
} from "@/services/api/invoiceDocuments";
import type {
  InvoiceDocument,
  InvoiceDocumentStatus,
  InvoiceMatchStatus,
} from "@/services/api/types";

const PAGE_SIZE = 20;

const STATUS_ORDER: InvoiceDocumentStatus[] = ["pending", "accepted", "queried", "rejected"];
const MATCH_ORDER: InvoiceMatchStatus[] = ["matched", "mismatch", "no_load", "no_rate_card", "no_amount"];

function statusVariant(status: string): BadgeProps["variant"] {
  if (status === "pending") return "warning";
  if (status === "accepted") return "success";
  if (status === "queried") return "info";
  if (status === "rejected") return "destructive";
  return "secondary";
}

function matchVariant(status: string): BadgeProps["variant"] {
  if (status === "matched") return "success";
  if (status === "mismatch") return "destructive";
  if (status === "no_load" || status === "no_rate_card" || status === "no_amount") {
    return "warning";
  }
  return "secondary";
}

function formatAmount(value: string | null): string {
  if (value === null || value === undefined) return "—";
  return `£${Number(value).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleDateString();
}

/** A pending row lets an administrator accept / query / reject with a note. */
function DecisionDialog({
  doc,
  onClose,
}: {
  doc: InvoiceDocument;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<InvoiceDocumentStatus>("accepted");
  const [note, setNote] = useState("");

  const decide = useMutation({
    mutationFn: () => decideInvoiceDocument(doc.id, status, note),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["invoice-documents"] });
      toast.success(t("invoices.decided"));
      onClose();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("invoices.decisionTitle")}</DialogTitle>
          <DialogDescription>
            {doc.invoice_number ?? doc.filename}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>{t("invoices.decision")}</Label>
            <Select value={status} onValueChange={(value) => setStatus(value as InvoiceDocumentStatus)}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="accepted">{t("invoices.accept")}</SelectItem>
                <SelectItem value="queried">{t("invoices.query")}</SelectItem>
                <SelectItem value="rejected">{t("invoices.reject")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>{t("invoices.note")}</Label>
            <Textarea
              rows={3}
              value={note}
              placeholder={t("invoices.notePlaceholder")}
              onChange={(event) => setNote(event.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button onClick={() => void decide.mutate()} disabled={decide.isPending}>
            {t("invoices.confirmDecision")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Edits the extracted values and re-matches the invoice to a load / rate card. */
function EditDialog({
  doc,
  onClose,
}: {
  doc: InvoiceDocument;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [invoiceNumber, setInvoiceNumber] = useState(doc.invoice_number ?? "");
  const [invoiceDate, setInvoiceDate] = useState(doc.invoice_date?.slice(0, 10) ?? "");
  const [amount, setAmount] = useState(doc.amount ?? "");
  const [currency, setCurrency] = useState(doc.currency ?? "GBP");
  const [supplier, setSupplier] = useState(doc.supplier_name ?? "");

  const save = useMutation({
    mutationFn: () =>
      updateInvoiceDocument(doc.id, {
        invoice_number: invoiceNumber.trim() || null,
        invoice_date: invoiceDate || null,
        amount: amount === "" ? null : Number(amount),
        currency: currency.trim().toUpperCase() || "GBP",
        supplier_name: supplier.trim() || null,
      }),
    onSuccess: (updated) => {
      void queryClient.invalidateQueries({ queryKey: ["invoice-documents"] });
      toast.success(
        t("invoices.editSaved", { match: t(`invoices.match.${updated.match_status}`) }),
      );
      onClose();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("invoices.editTitle")}</DialogTitle>
          <DialogDescription>{doc.filename}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="edit-invoice-number">{t("invoices.invoiceNumber")}</Label>
            <Input
              id="edit-invoice-number"
              value={invoiceNumber}
              onChange={(event) => setInvoiceNumber(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-invoice-date">{t("invoices.invoiceDate")}</Label>
            <Input
              id="edit-invoice-date"
              type="date"
              value={invoiceDate}
              onChange={(event) => setInvoiceDate(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-amount">{t("invoices.amount")}</Label>
            <Input
              id="edit-amount"
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-currency">{t("invoices.currency")}</Label>
            <Input
              id="edit-currency"
              maxLength={3}
              value={currency}
              onChange={(event) => setCurrency(event.target.value.toUpperCase())}
            />
          </div>
          <div className="col-span-2 space-y-2">
            <Label htmlFor="edit-supplier">{t("invoices.supplier")}</Label>
            <Input
              id="edit-supplier"
              value={supplier}
              onChange={(event) => setSupplier(event.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button onClick={() => void save.mutate()} disabled={save.isPending}>
            {t("invoices.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Read-only detail: extracted fields, price-check outcome and decision. */
function DetailDialog({ doc, onClose }: { doc: InvoiceDocument; onClose: () => void }) {
  const { t } = useTranslation();
  const extracted = Object.entries(doc.extracted_fields ?? {});

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("invoices.detailTitle")}</DialogTitle>
          <DialogDescription>{doc.filename}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <span className="text-muted-foreground">{t("invoices.invoiceNumber")}</span>
          <span>{doc.invoice_number ?? "—"}</span>
          <span className="text-muted-foreground">{t("invoices.supplier")}</span>
          <span>{doc.supplier_name ?? "—"}</span>
          <span className="text-muted-foreground">{t("invoices.loadRef")}</span>
          <span>{doc.load_ref ?? "—"}</span>
          <span className="text-muted-foreground">{t("invoices.customer")}</span>
          <span>{doc.customer_name ?? "—"}</span>
          <span className="text-muted-foreground">{t("invoices.amount")}</span>
          <span>{formatAmount(doc.amount)}</span>
          <span className="text-muted-foreground">{t("invoices.expected")}</span>
          <span>{formatAmount(doc.expected_amount)}</span>
          <span className="text-muted-foreground">{t("invoices.columnMatch")}</span>
          <span>
            <Badge variant={matchVariant(doc.match_status)}>
              {t(`invoices.match.${doc.match_status}`)}
            </Badge>
          </span>
          <span className="text-muted-foreground">{t("invoices.columnStatus")}</span>
          <span>
            <Badge variant={statusVariant(doc.status)}>{t(`invoices.status.${doc.status}`)}</Badge>
          </span>
          <span className="text-muted-foreground">{t("invoices.uploadedAt")}</span>
          <span>{new Date(doc.uploaded_at).toLocaleString()}</span>
        </div>
        {doc.match_detail ? (
          <p className="rounded-md border bg-muted/30 p-3 text-sm text-muted-foreground">
            <span className="font-medium text-foreground">{t("invoices.matchDetail")}: </span>
            {doc.match_detail}
          </p>
        ) : null}
        {doc.decision_note ? (
          <p className="rounded-md border bg-muted/30 p-3 text-sm text-muted-foreground">
            <span className="font-medium text-foreground">{t("invoices.decisionNote")}: </span>
            {doc.decision_note}
          </p>
        ) : null}
        {extracted.length > 0 ? (
          <div>
            <p className="mb-2 text-sm font-medium">{t("invoices.extractedFields")}</p>
            <div className="flex flex-wrap gap-2">
              {extracted.map(([key, value]) => (
                <Badge key={key} variant="outline">
                  {key}: {String(value)}
                </Badge>
              ))}
            </div>
          </div>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t("common.close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Uploaded supplier invoices: price-checked against rate cards, then decided. */
export function InvoicesPage() {
  const { t } = useTranslation();
  const { has } = usePermissions();
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);

  const [offset, setOffset] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [matchFilter, setMatchFilter] = useState<string>("all");
  const [deciding, setDeciding] = useState<InvoiceDocument | null>(null);
  const [editing, setEditing] = useState<InvoiceDocument | null>(null);
  const [viewing, setViewing] = useState<InvoiceDocument | null>(null);

  const canWrite = has(Permission.INVOICES_WRITE);

  const query = useQuery({
    queryKey: ["invoice-documents", { offset, statusFilter, matchFilter }],
    queryFn: () =>
      listInvoiceDocuments({
        limit: PAGE_SIZE,
        offset,
        status: statusFilter === "all" ? undefined : (statusFilter as InvoiceDocumentStatus),
        match_status: matchFilter === "all" ? undefined : matchFilter,
      }),
  });

  const upload = useMutation({
    mutationFn: (file: File) => uploadInvoicePdf(file),
    onSuccess: (run) => {
      void queryClient.invalidateQueries({ queryKey: ["invoice-documents"] });
      void queryClient.invalidateQueries({ queryKey: ["import-runs"] });
      if (run.rows_quarantined > 0) {
        toast.error(t("invoices.uploadQuarantined"));
      } else if (run.rows_applied === 0) {
        toast.info(t("invoices.uploadNoop"));
      } else {
        toast.success(t("invoices.uploaded"));
      }
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const columns: ColumnDef<InvoiceDocument>[] = [
    {
      header: t("invoices.invoiceNumber"),
      cell: ({ row }) => (
        <button
          className="font-medium text-primary hover:underline"
          onClick={() => setViewing(row.original)}
        >
          {row.original.invoice_number ?? row.original.filename}
        </button>
      ),
    },
    {
      header: t("invoices.supplier"),
      cell: ({ row }) => <span>{row.original.supplier_name ?? "—"}</span>,
    },
    {
      header: t("invoices.loadRef"),
      cell: ({ row }) => <span>{row.original.load_ref ?? "—"}</span>,
    },
    {
      header: t("invoices.customer"),
      cell: ({ row }) => <span>{row.original.customer_name ?? "—"}</span>,
    },
    {
      header: t("invoices.amount"),
      cell: ({ row }) => <span className="font-medium">{formatAmount(row.original.amount)}</span>,
    },
    {
      header: t("invoices.expected"),
      cell: ({ row }) => <span>{formatAmount(row.original.expected_amount)}</span>,
    },
    {
      header: t("invoices.columnMatch"),
      cell: ({ row }) => (
        <Badge variant={matchVariant(row.original.match_status)}>
          {t(`invoices.match.${row.original.match_status}`)}
        </Badge>
      ),
    },
    {
      header: t("invoices.columnStatus"),
      cell: ({ row }) => (
        <Badge variant={statusVariant(row.original.status)}>
          {t(`invoices.status.${row.original.status}`)}
        </Badge>
      ),
    },
    {
      header: t("invoices.uploadedAt"),
      cell: ({ row }) => <span>{formatDate(row.original.uploaded_at)}</span>,
    },
    {
      header: t("common.actions"),
      cell: ({ row }) => {
        const doc = row.original;
        return (
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" onClick={() => setViewing(doc)} title={t("invoices.view")}>
              <Eye className="h-4 w-4" />
            </Button>
            {canWrite ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setEditing(doc)}
                title={t("invoices.edit")}
              >
                <Pencil className="h-4 w-4" />
              </Button>
            ) : null}
            {canWrite && doc.status === "pending" ? (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-success"
                  onClick={() => {
                    setDeciding(doc);
                  }}
                  title={t("invoices.accept")}
                >
                  <Check className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setDeciding(doc)}
                  title={t("invoices.query")}
                >
                  <CircleHelp className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-destructive"
                  onClick={() => setDeciding(doc)}
                  title={t("invoices.reject")}
                >
                  <X className="h-4 w-4" />
                </Button>
              </>
            ) : null}
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{t("invoices.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("invoices.subtitle")}</p>
        </div>
        {canWrite ? (
          <div className="flex items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf,.pdf"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) {
                  void upload.mutate(file);
                }
                event.target.value = "";
              }}
            />
            <Button onClick={() => fileRef.current?.click()} disabled={upload.isPending}>
              <FileUp className="h-4 w-4" />
              {t("invoices.upload")}
            </Button>
          </div>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-4 text-sm">
        <div className="flex items-center gap-2">
          <Label className="whitespace-nowrap">{t("invoices.statusFilter")}</Label>
          <Select
            value={statusFilter}
            onValueChange={(value) => {
              setStatusFilter(value);
              setOffset(0);
            }}
          >
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("invoices.all")}</SelectItem>
              {STATUS_ORDER.map((value) => (
                <SelectItem key={value} value={value}>
                  {t(`invoices.status.${value}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Label className="whitespace-nowrap">{t("invoices.matchFilter")}</Label>
          <Select
            value={matchFilter}
            onValueChange={(value) => {
              setMatchFilter(value);
              setOffset(0);
            }}
          >
            <SelectTrigger className="w-52">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("invoices.all")}</SelectItem>
              {MATCH_ORDER.map((value) => (
                <SelectItem key={value} value={value}>
                  {t(`invoices.match.${value}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {query.isLoading ? (
        <div className="rounded-md border p-8 text-sm text-muted-foreground">{t("common.loading")}</div>
      ) : (query.data?.items ?? []).length === 0 ? (
        <EmptyState title={t("invoices.empty")} />
      ) : (
        <DataTable
          columns={columns}
          data={query.data?.items ?? []}
          total={query.data?.total ?? 0}
          limit={PAGE_SIZE}
          offset={offset}
          onPageChange={setOffset}
          onRowDoubleClick={(row) => setViewing(row)}
        />
      )}

      {deciding ? (
        <DecisionDialog doc={deciding} onClose={() => setDeciding(null)} />
      ) : null}
      {editing ? <EditDialog doc={editing} onClose={() => setEditing(null)} /> : null}
      {viewing ? <DetailDialog doc={viewing} onClose={() => setViewing(null)} /> : null}
    </div>
  );
}
